/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';

import api from '~/services/api';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface IProgressData {
  event: 'start' | 'progress' | 'done' | 'error';
  total?: number;
  current?: number;
  sent?: number;
  failed?: number;
  ticket_email?: string;
  ticket_status?: 'sent' | 'failed';
  error?: string;
  message?: string;
  errors?: string[];
}

export interface IDispatchRecord {
  id: number;
  event_id: number;
  email: string;
  name: string;
  subject: string;
  type: string;
  status: 'queued' | 'sent' | 'failed' | 'opened';
  opened_at: string | null;
  open_count: number;
  tracking_pixel_url: string;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface IDispatchState {
  eventId: number | null;
  eventName: string;
  emailType: string;
  isLoading: boolean;
  progress: IProgressData | null;
  result: IProgressData | null;
  dispatches: IDispatchRecord[];
  isPolling: boolean;
}

interface IBulkEmailContextData {
  state: IDispatchState;
  dispatch: (
    eventId: number,
    emailType: string,
    eventName: string,
    deliveryKitsInfo?: string,
    customData?: {
      subject: string;
      htmlBody: string;
    }
  ) => void;
  cancel: () => void;
  fetchDispatches: (eventId: number) => Promise<void>;
  clearResult: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const BulkEmailContext = createContext<IBulkEmailContextData>(
  {} as IBulkEmailContextData
);

// ─── Styled Components (Floating Card) ───────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FloatingCardWrapper = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  width: 340px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  padding: 16px 20px;
  cursor: pointer;
  animation: ${fadeIn} 0.3s ease;
  border: 1px solid #e1e1e1;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
  }

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;

    .event-name {
      font-size: 13px;
      font-weight: 600;
      color: #202020;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 240px;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 18px;
      color: #8c8c8c;
      cursor: pointer;
      padding: 0 4px;
      line-height: 1;

      &:hover {
        color: #202020;
      }
    }
  }

  .card-status {
    font-size: 12px;
    color: #ff6900;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .card-status.done {
    color: #00b15c;
  }

  .card-status.error {
    color: #dc3545;
  }

  .progress-bar-container {
    width: 100%;
    height: 8px;
    background: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 8px;
  }

  .progress-bar-fill {
    height: 100%;
    background: #ff6900;
    transition: width 0.3s ease;
    border-radius: 4px;
  }

  .progress-bar-fill.done {
    background: #00b15c;
  }

  .card-counters {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
    color: #5e5e5e;

    .counter-sent {
      color: #00b15c;
      font-weight: 500;
    }

    .counter-failed {
      color: #dc3545;
      font-weight: 500;
    }

    .counter-progress {
      color: #8c8c8c;
    }
  }
`;

// ─── Floating Card Component ──────────────────────────────────────────────────

interface IFloatingCardProps {
  state: IDispatchState;
  onClose: () => void;
}

const EmailDispatchFloatingCard: React.FC<IFloatingCardProps> = ({
  state,
  onClose,
}) => {
  const history = useHistory();
  const { progress, result, eventName } = state;

  const total = result?.total || progress?.total || 0;
  const current = result?.current || progress?.current || 0;
  const sent = result?.sent || progress?.sent || 0;
  const failed = result?.failed || progress?.failed || 0;
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;

  const isDone = result?.event === 'done';
  const isError = result?.event === 'error';

  let statusText = 'Enfileirando...';
  let statusClass = '';
  if (isDone) {
    statusText = 'Concluído';
    statusClass = 'done';
  } else if (isError) {
    statusText = result?.message || 'Erro no disparo';
    statusClass = 'error';
  }

  const handleClick = useCallback(() => {
    history.push(`${process.env.PUBLIC_URL}/disparo-emails`);
  }, [history]);

  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClose();
    },
    [onClose]
  );

  return (
    <FloatingCardWrapper onClick={handleClick}>
      <div className="card-header">
        <span className="event-name" title={eventName}>
          📧 {eventName}
        </span>
        <button type="button" className="close-btn" onClick={handleClose}>
          ×
        </button>
      </div>
      <div className={`card-status ${statusClass}`}>{statusText}</div>
      <div className="progress-bar-container">
        <div
          className={`progress-bar-fill ${isDone ? 'done' : ''}`}
          style={{ width: `${isDone ? 100 : percentage}%` }}
        />
      </div>
      <div className="card-counters">
        <span className="counter-progress">
          {current}/{total}
        </span>
        <span className="counter-sent">✅ {sent}</span>
        <span className="counter-failed">❌ {failed}</span>
      </div>
    </FloatingCardWrapper>
  );
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export const BulkEmailProvider: React.FC = ({ children }) => {
  const [state, setState] = useState<IDispatchState>({
    eventId: null,
    eventName: '',
    emailType: '',
    isLoading: false,
    progress: null,
    result: null,
    dispatches: [],
    isPolling: false,
  });

  const [showCard, setShowCard] = useState(false);
  const cancelSourceRef = useRef<ReturnType<
    typeof axios.CancelToken.source
  > | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-hide card 10s after done/error
  useEffect(() => {
    if (state.result?.event === 'done' || state.result?.event === 'error') {
      autoHideRef.current = setTimeout(() => {
        setShowCard(false);
      }, 10000);
    }

    return () => {
      if (autoHideRef.current) {
        clearTimeout(autoHideRef.current);
      }
    };
  }, [state.result]);

  // Polling after dispatch is done
  useEffect(() => {
    if (state.result?.event !== 'done' || !state.eventId) return undefined;

    const hasQueued = state.dispatches.some((d) => d.status === 'queued');
    if (!hasQueued && state.dispatches.length > 0) {
      setState((prev) => ({ ...prev, isPolling: false }));
      return undefined;
    }

    setState((prev) => ({ ...prev, isPolling: true }));

    pollingRef.current = setInterval(async () => {
      try {
        const response = await api.get<IDispatchRecord[]>(
          `/bulk-emails/${state.eventId}/dispatches`
        );
        const { data } = response;
        setState((prev) => ({ ...prev, dispatches: data }));

        const stillQueued = data.some((d) => d.status === 'queued');
        if (!stillQueued && pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setState((prev) => ({ ...prev, isPolling: false }));
        }
      } catch {
        // Stop polling on error to avoid infinite loop
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        setState((prev) => ({ ...prev, isPolling: false }));
      }
    }, 30000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      setState((prev) => ({ ...prev, isPolling: false }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.result?.event, state.eventId]);

  const cancel = useCallback(() => {
    cancelSourceRef.current?.cancel();
    setState((prev) => ({ ...prev, isLoading: false }));
  }, []);

  const fetchDispatches = useCallback(async (eventId: number) => {
    try {
      const response = await api.get<IDispatchRecord[]>(
        `/bulk-emails/${eventId}/dispatches`
      );
      setState((prev) => ({ ...prev, dispatches: response.data }));
    } catch {
      // silently ignore
    }
  }, []);

  const dispatch = useCallback(
    async (
      eventId: number,
      emailType: string,
      eventName: string,
      deliveryKitsInfo?: string,
      customData?: {
        subject: string;
        htmlBody: string;
      }
    ) => {
      setState((prev) => ({
        ...prev,
        eventId,
        eventName,
        emailType,
        isLoading: true,
        progress: null,
        result: null,
        dispatches: [],
        isPolling: false,
      }));
      setShowCard(true);

      if (autoHideRef.current) {
        clearTimeout(autoHideRef.current);
      }
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }

      const source = axios.CancelToken.source();
      cancelSourceRef.current = source;

      let lastIndex = 0;

      try {
        await api.post(
          `/bulk-emails/${eventId}/reminder`,
          {
            type: emailType,
            ...(deliveryKitsInfo ? { kit_pickup_html: deliveryKitsInfo } : {}),
            ...(customData ? { custom_data: customData } : {}),
          },
          {
            cancelToken: source.token,
            responseType: 'text',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onDownloadProgress: (progressEvent: any) => {
              const xhr = progressEvent.target || progressEvent.currentTarget;
              const text: string = xhr?.responseText || xhr?.response || '';
              const newText = text.substring(lastIndex);
              lastIndex = text.length;

              const lines = newText.split('\n');
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const data: IProgressData = JSON.parse(line.slice(6));

                    if (data.event === 'progress' || data.event === 'start') {
                      setState((prev) => ({ ...prev, progress: data }));
                    } else if (data.event === 'done') {
                      setState((prev) => ({
                        ...prev,
                        result: data,
                        isLoading: false,
                      }));
                    } else if (data.event === 'error') {
                      setState((prev) => ({
                        ...prev,
                        result: data,
                        isLoading: false,
                      }));
                    }
                  } catch {
                    // ignore parse errors
                  }
                }
              }
            },
          }
        );

        // Fetch initial dispatches after stream ends
        await fetchDispatches(eventId);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        if (!axios.isCancel(err)) {
          setState((prev) => ({
            ...prev,
            result: { event: 'error', message: err.message },
            isLoading: false,
          }));
        }
      } finally {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [fetchDispatches]
  );

  const clearResult = useCallback(() => {
    setState({
      eventId: null,
      eventName: '',
      emailType: '',
      isLoading: false,
      progress: null,
      result: null,
      dispatches: [],
      isPolling: false,
    });
    setShowCard(false);
  }, []);

  const handleCloseCard = useCallback(() => {
    setShowCard(false);
  }, []);

  return (
    <BulkEmailContext.Provider
      value={{ state, dispatch, cancel, fetchDispatches, clearResult }}
    >
      {children}
      {showCard && (state.isLoading || state.result) && (
        <EmailDispatchFloatingCard state={state} onClose={handleCloseCard} />
      )}
    </BulkEmailContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useBulkEmailDispatch(): IBulkEmailContextData {
  const context = useContext(BulkEmailContext);
  return context;
}
